import { useState } from "react";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntitlementCard } from "./EntitlementCard";
import { AbsenceCarryOverCard } from "./AbsenceCarryOverCard";
import { OvertimeSummaryCard } from "./OvertimeSummaryCard";
import { AbsenceTable } from "./AbsenceTable";
import { AbsenceForm } from "./AbsenceForm";
import { OvertimePayoutForm } from "./OvertimePayoutForm";
import {
  useEmployeeAbsences,
  useEmployeeEntitlements,
  useCreateEmployeeAbsence,
  useUpdateEmployeeAbsence,
  useDeleteEmployeeAbsence,
  EmployeeAbsence,
} from "@/hooks/useEmployeeAbsences";
import { useEmploymentPeriods } from "@/hooks/useEmploymentPeriods";
import { usePublicHolidays } from "@/hooks/usePublicHolidays";
import { computeYearlyEmploymentPct } from "./EntitlementCard";

interface AbsencesOvertimePanelProps {
  personId: string;
  year: number;
  onYearChange: (year: number) => void;
  /** Allow add/edit/delete on absences. Defaults to true. */
  editAbsences?: boolean;
  /** Show overtime payout form (admin). Defaults to true. */
  showPayouts?: boolean;
}

export function AbsencesOvertimePanel({
  personId,
  year,
  onYearChange,
  editAbsences = true,
  showPayouts = true,
}: AbsencesOvertimePanelProps) {
  const thisYear = new Date().getFullYear();

  const { data: absences = [] } = useEmployeeAbsences(personId, year);
  const { data: periods = [] } = useEmploymentPeriods(personId);
  const { data: publicHolidays = [] } = usePublicHolidays(year);
  const createAbsence = useCreateEmployeeAbsence();
  const updateAbsence = useUpdateEmployeeAbsence();
  const deleteAbsence = useDeleteEmployeeAbsence();

  const publicHolidayDates = new Set(publicHolidays.map((h) => h.date));
  const employmentPct = computeYearlyEmploymentPct(periods, year);

  const [absenceFormOpen, setAbsenceFormOpen] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState<
    EmployeeAbsence | undefined
  >();

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onYearChange(year - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium w-12 text-center">{year}</span>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onYearChange(year + 1)}
            disabled={year >= thisYear}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <EntitlementCard personId={personId} year={year} />
          <OvertimeSummaryCard personId={personId} year={year} />
        </div>

        {year < thisYear && (
          <AbsenceCarryOverCard personId={personId} year={year} />
        )}

        <Card>
          <div className="flex items-center justify-between p-6 pb-3">
            <h3 className="text-base font-medium">Absences {year}</h3>
            {editAbsences && (
              <Button
                size="sm"
                onClick={() => {
                  setEditingAbsence(undefined);
                  setAbsenceFormOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Absence
              </Button>
            )}
          </div>
          <CardContent>
            <AbsenceTable
              absences={absences}
              publicHolidayDates={publicHolidayDates}
              employmentPct={employmentPct}
              showActions={editAbsences}
              onEdit={
                editAbsences
                  ? (h) => {
                      setEditingAbsence(h);
                      setAbsenceFormOpen(true);
                    }
                  : undefined
              }
              onDelete={
                editAbsences ? (hid) => deleteAbsence.mutate(hid) : undefined
              }
            />
          </CardContent>
        </Card>

        {showPayouts && <OvertimePayoutForm personId={personId} year={year} />}
      </div>

      {editAbsences && (
        <Dialog open={absenceFormOpen} onOpenChange={setAbsenceFormOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingAbsence ? "Edit Absence" : "Add Absence"}
              </DialogTitle>
            </DialogHeader>
            <AbsenceForm
              absence={editingAbsence}
              onSubmit={(data) => {
                if (editingAbsence) {
                  updateAbsence.mutate({
                    id: editingAbsence.id,
                    updates: data,
                  });
                } else {
                  if (
                    !data.absence_type ||
                    !data.start_date ||
                    !data.end_date
                  ) {
                    return;
                  }
                  createAbsence.mutate({
                    ...data,
                    person_id: personId,
                    absence_type: data.absence_type,
                    start_date: data.start_date,
                    end_date: data.end_date,
                  });
                }
                setAbsenceFormOpen(false);
              }}
              onCancel={() => setAbsenceFormOpen(false)}
              isLoading={createAbsence.isPending || updateAbsence.isPending}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
